// A bench behind one adapter, whatever hosts it: a room wall (`polyBenches`),
// one face of a partition (`InnerWall.benches[face]`), or nothing — a standalone
// sauna bench, the object itself. The bench panel, the Bench tool, Delete and the
// handle drag read and write a bench through `benchOf`, so a write or a removal
// is the same on every host. DOM-free. docs/claude/benches.md, *One bench,
// whatever hosts it*.
import { cloneSkirting } from '../shared/benchPresets';
import {
  type InnerWall,
  type ProductInstance,
  type SkirtingSpec,
} from '../shared/types';
import {
  autoTierFor,
  benchDepthFromSlats,
  type BenchTier,
  MIN_BENCH_DEPTH,
} from './benches';
import {
  benchJoinSlots,
  dropBenchJoinSlots,
  polyBenchBands,
  polyBenchLifts,
  polyInnerBenchBands,
} from './polyRoom';

/** A bench on a room wall (`polyBenches`). */
export type PolyBench = NonNullable<ProductInstance['polyBenches']>[number];
/** A bench on a partition face (`InnerWall.benches`). */
export type ProductBenchLeg = NonNullable<NonNullable<InnerWall['benches']>['a']>[number];

/** A bench's boards as the slat table, the skirting card and a preset write
 *  them. A key present is written, even as undefined (which clears it). */
export interface SlatPatch { slats?: number[]; gaps?: number[]; slatThickness?: number[]; overlap?: number; slatPresetId?: string; skirting?: boolean; skirtingBoards?: SkirtingSpec; skirtingSetBack?: number }

/** What a write to a room's bench carries: its boards, and its depth, its span
 *  along its wall (`start` / `end`, written when a number), its seat height and
 *  its colour. A key present is written, even as undefined (which clears it, the
 *  tier's default taking over). */
export interface BenchPatch extends SlatPatch { depth?: number; start?: number; end?: number; height?: number; color?: string }

/** The fields a slat patch writes. */
interface SlatHost { slats?: number[]; gaps?: number[]; slatThickness?: number[]; overlap?: number; slatPresetId?: string; skirting?: boolean; skirtingBoards?: SkirtingSpec; skirtingSetBack?: number; depth: number }

const SLAT_KEYS: ReadonlyArray<keyof SlatPatch> = ['slats', 'gaps', 'slatThickness', 'overlap', 'slatPresetId', 'skirting', 'skirtingBoards', 'skirtingSetBack'];

/** Write a patch's skirting onto a bench (or a preset): each key written when
 *  present, as the rest of a slat patch. */
export function applyBenchSkirting(host: Omit<SlatHost, 'depth'>, patch: SlatPatch): void {
  if ('skirting' in patch) host.skirting = patch.skirting || undefined;
  if ('skirtingBoards' in patch) host.skirtingBoards = patch.skirtingBoards ? cloneSkirting(patch.skirtingBoards) : undefined;
  if ('skirtingSetBack' in patch) host.skirtingSetBack = patch.skirtingSetBack;
}

/** Write a slat patch onto any bench — a wall's, a partition's, a standalone
 *  sauna bench: keys present are written, and the depth follows the slats. */
export function applySlats(host: SlatHost, patch: SlatPatch): void {
  if ('slats' in patch) host.slats = patch.slats ? [...patch.slats] : undefined;
  if ('gaps' in patch) host.gaps = patch.gaps ? [...patch.gaps] : undefined;
  if ('slatThickness' in patch) host.slatThickness = patch.slatThickness ? [...patch.slatThickness] : undefined;
  if ('overlap' in patch) host.overlap = patch.overlap;
  if ('slatPresetId' in patch) host.slatPresetId = patch.slatPresetId;
  applyBenchSkirting(host, patch);
  const d = benchDepthFromSlats(host);
  if (d != null) host.depth = Math.max(MIN_BENCH_DEPTH, d);
}

/** Write a bench patch onto a room's bench, the same on a wall and a partition:
 *  the depth whole mm, never under `MIN_BENCH_DEPTH`; the height whole mm; a
 *  slat patch as `applySlats` writes it (the depth then following the slats). */
function writeLeg(leg: PolyBench | ProductBenchLeg, patch: BenchPatch): void {
  if (patch.depth != null) leg.depth = Math.max(MIN_BENCH_DEPTH, Math.round(patch.depth));
  if (patch.start != null) leg.start = patch.start;
  if (patch.end != null) leg.end = patch.end;
  if ('height' in patch) leg.height = patch.height == null ? undefined : Math.max(0, Math.round(patch.height));
  if ('color' in patch) leg.color = patch.color;
  if (SLAT_KEYS.some((k) => k in patch)) applySlats(leg, patch);
}

/** Where a room's bench stands: on a wall (numbered by WALL), or on a face of a
 *  partition (`iwIndex` into `innerWalls`). */
export type BenchHost =
  | { kind: 'partition'; iwIndex: number; face: 'a' | 'b' }
  | { kind: 'wall'; edge: number };

/** A room's bench, whatever hosts it. `write` and `remove` find the bench again
 *  by its id, so an adapter outlives a regeneration that replaced the object. */
export interface RoomBench {
  readonly room: ProductInstance;
  readonly id: string;
  readonly host: BenchHost;
  /** The stored bench, as found when the adapter was made. */
  readonly leg: PolyBench | ProductBenchLeg;
  /** Its place in its stack: 0 against its wall or partition face. */
  readonly tierIndex: number;
  /** Its level (low / medium / high), which its default colour and height
   *  follow. */
  readonly level: BenchTier;
  /** How far a profiled tier rides up on the one in front (mm), else 0. */
  readonly lift: number;
  /** Write a patch onto it (`writeLeg`). The caller regenerates the room after
   *  (`editRoom`; a drag at its release), in the edit's one undo step. */
  write(patch: BenchPatch): void;
  /** Take it out of its host's list (the list stays, empty or not), its joints
   *  re-keyed (`dropBenchJoinSlots`). The caller regenerates the room after. */
  remove(): void;
}

/** A standalone sauna bench (a node run): the object is the bench. */
export interface StandaloneBench {
  readonly id: string;
  readonly host: { kind: 'standalone' };
  readonly leg: ProductInstance;
  write(patch: SlatPatch): void;
}

export type BenchRef = RoomBench | StandaloneBench;

/** The bench `id` of a room as stored, on its wall or its partition, and its
 *  host; null when the room has no such bench. */
function locate(room: ProductInstance, id: string): { leg: PolyBench | ProductBenchLeg; host: BenchHost; index: number } | null {
  const wall = (room.polyBenches ?? []).find((b) => b.id === id);
  if (wall) return { leg: wall, host: { kind: 'wall', edge: wall.edge }, index: -1 };
  const walls = room.innerWalls ?? [];
  for (let iwIndex = 0; iwIndex < walls.length; iwIndex++) {
    for (const face of ['a', 'b'] as const) {
      const list = walls[iwIndex].benches?.[face] ?? [];
      const index = list.findIndex((t) => t.id === id);
      if (index >= 0) return { leg: list[index], host: { kind: 'partition', iwIndex, face }, index };
    }
  }
  return null;
}

/** The stored bench `id` of a room — a wall's or a partition's — or undefined. */
export function benchLegOf(room: ProductInstance, id: string): PolyBench | ProductBenchLeg | undefined {
  return locate(room, id)?.leg;
}

/** A room's bench `id` — on a wall or a partition — behind the one adapter, or
 *  null when the room has none. */
export function roomBenchOf(room: ProductInstance, id: string): RoomBench | null {
  const found = locate(room, id);
  if (!found) return null;
  const { leg, host } = found;
  const partBand = () => (host.kind === 'partition' ? polyInnerBenchBands(room).find((b) => b.id === id) : undefined);
  const wallBand = () => (host.kind === 'wall' ? polyBenchBands(room).find((b) => b.id === id) : undefined);
  return {
    room, id, host, leg,
    get tierIndex() { return host.kind === 'wall' ? (wallBand()?.tier ?? 0) : found.index; },
    get level() {
      if (host.kind === 'wall') return wallBand()?.level ?? 'low';
      const count = room.innerWalls?.[host.iwIndex]?.benches?.[host.face]?.length ?? 1;
      return partBand()?.level ?? autoTierFor(found.index, count);
    },
    get lift() { return host.kind === 'wall' ? (polyBenchLifts(room).get(id) ?? 0) : (partBand()?.lift ?? 0); },
    write(patch) {
      const now = locate(room, id);
      if (now) writeLeg(now.leg, patch);
    },
    remove() {
      const now = locate(room, id);
      if (!now) return;
      const slots = benchJoinSlots(room, id);
      if (now.host.kind === 'wall') room.polyBenches = (room.polyBenches ?? []).filter((b) => b.id !== id);
      else {
        const benches = room.innerWalls![now.host.iwIndex].benches!;
        benches[now.host.face] = (benches[now.host.face] ?? []).filter((t) => t.id !== id);
      }
      dropBenchJoinSlots(room, slots);
    },
  };
}

/** Any bench behind the one adapter: a room's bench `id` (`roomBenchOf`), or a
 *  standalone sauna bench asked for by its own id. Null when there is none. */
export function benchOf(inst: ProductInstance, id: string): BenchRef | null {
  if (inst.category === 'saunabench' && inst.id === id && !inst.autoBenches) {
    return { id, host: { kind: 'standalone' }, leg: inst, write: (patch: SlatPatch) => applySlats(inst, patch) };
  }
  return roomBenchOf(inst, id);
}
